import {
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Link,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { ArrowRight } from "@material-ui/icons";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import { Alert } from "@material-ui/lab";
import React, { useState } from "react";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { Link as RouterLink } from "react-router-dom";

import { useAlert } from "react-alert";

import { handleStartMasquerading } from "../MasqueradePage";
import Navigation from "../Navigation";
import { theme } from "../theme";
import { userIsFPEmployee } from "../util";
import useListOnboardedDomains from "./queries/useListOnboardedDomains";
import useWTMURLs from "./useWTMURLs";

const useStyles = makeStyles((theme) => ({
  breadcrumb: {
    marginTop: 5,
    marginBottom: 10,
  },
  card: {
    minHeight: 500,
    position: "relative",
    width: "100%",
  },
  cardContent: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  cardHeader: {
    padding: "1em",
  },
  cardNav: {
    padding: "1em",
  },
  formLabel: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  loader: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translateX(-50%) translateY(-50%)",
  },
  navigation: {
    paddingTop: 12,
  },
  pageTitle: {
    paddingTop: 12,
  },
}));

function CopyToClipboardButton({
  text,
  title,
}: {
  text: string;
  title: string;
}) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Tooltip title={copied ? "Copied!" : title}>
      <Typography
        component="span"
        variant="h4"
        style={{ cursor: "pointer" }}
        onClick={copyToClipboard}
      >
        &#x2398;
      </Typography>
    </Tooltip>
  );
}

function DomainAdminDashboard() {
  const alert = useAlert();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const classes = useStyles();
  const history = useHistory();
  const wtmURLs = useWTMURLs();

  const { data, isLoading } = useListOnboardedDomains();

  if (!userIsFPEmployee()) {
    history.push(wtmURLs.trackersOverview);
    return null;
  }

  const rows = data?.domains ?? [];

  return (
    <Navigation>
      <div className={classes.pageTitle}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          className={classes.breadcrumb}
        >
          <RouterLink color="inherit" to={wtmURLs.trackersOverview}>
            <Typography variant="overline">Web Tracker Manager</Typography>
          </RouterLink>
          <Typography variant="overline">Domain Admin</Typography>
        </Breadcrumbs>
        <Typography component="h1" gutterBottom variant="h3">
          Domain Admin
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          This page is a readonly list of domains onboarded to Web Tracker
          Manager, but will soon feature more onboarding tools.
        </Typography>
        <Alert
          severity="info"
          style={{
            border: `1px solid ${theme.palette.grey["300"]}`,
            marginBottom: theme.spacing(2),
          }}
        >
          To set up Web Tracker Monitoring for a domain, masquerade into that
          customer's account, select the Freshpaint environment relevant to the
          customer's site, and use the{" "}
          <Link>
            <strong>
              <RouterLink to="/admin/trackers/setUpDomain">
                Set up Web Tracker Monitoring admin tool
              </RouterLink>
            </strong>
          </Link>
          .
        </Alert>
      </div>

      <Card className={classes.card}>
        {isLoading ? (
          <CircularProgress className={classes.loader} />
        ) : (
          <>
            <CardHeader
              className={classes.cardHeader}
              title="Web Tracker Manager Domains"
            />

            <Divider />

            <CardContent className={classes.cardContent}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Typography variant="h6">Domain</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6">
                          Go to Web Tracker Manager
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6">Account</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6">Project</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6">Environment</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6">Emails</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rows.map((d) => (
                      <TableRow key={`wtm-domains-admin-row-${d.domain}`}>
                        <TableCell>
                          <Link
                            href={`https://${d.domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: theme.palette.primary.main,
                              fontWeight: "bold",
                              textDecoration: "underline",
                            }}
                          >
                            {d.domain}
                          </Link>
                        </TableCell>

                        <TableCell>
                          <Button
                            color="primary"
                            onClick={() =>
                              handleStartMasquerading(
                                alert,
                                dispatch,
                                queryClient,
                                d.envID,
                                wtmURLs.trackersOverview
                              )
                            }
                            variant="contained"
                          >
                            <ArrowRight />
                          </Button>
                        </TableCell>

                        <TableCell>
                          <Typography variant="overline">Account ID</Typography>
                          <Typography variant="body1">
                            {d.accountID}{" "}
                            <CopyToClipboardButton
                              text={d.accountID}
                              title="Copy Account ID"
                            />
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="overline">
                            {d.projName}
                          </Typography>
                          <Typography variant="body1">
                            {d.projID}{" "}
                            <CopyToClipboardButton
                              text={d.projID}
                              title="Copy Project ID"
                            />
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="overline">
                            {d.envName}
                          </Typography>
                          <Typography variant="body1">
                            {d.envID}{" "}
                            <CopyToClipboardButton
                              text={d.envID}
                              title="Copy Environment ID"
                            />
                          </Typography>
                        </TableCell>

                        <TableCell>
                          {d.emails && (
                            <Typography variant="body1">
                              {d.emails.join(", ")}{" "}
                              <CopyToClipboardButton
                                text={d.emails.join(", ")}
                                title="Copy Emails"
                              />
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </>
        )}
      </Card>
    </Navigation>
  );
}

export default DomainAdminDashboard;
